import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';

// A map of our supported languages for the dropdown.
const supportedLanguages = {
  en: 'English', es: 'Español', fr: 'Français', de: 'Deutsch', it: 'Italiano',
  ja: '日本語', ko: '한국어', pt: 'Português', ru: 'Русский', zh: '中文'
};

// IMPORTANT: Replace this with your actual Cloudflare Worker URL
const CLOUDFLARE_WORKER_URL = 'https://speedtest-worker.kasmi3ab9or.workers.dev';

function App() {
    const { t, i18n } = useTranslation();

    const [testState, setTestState] = useState('idle');
    const [downloadSpeed, setDownloadSpeed] = useState(0);
    const [uploadSpeed, setUploadSpeed] = useState(0);
    const [latency, setLatency] = useState(0);

    const runTest = useCallback(() => {
        setTestState('testing');
        setDownloadSpeed(0);
        setUploadSpeed(0);
        setLatency(0);

        // --- LATENCY TEST (to your CapRover Server) ---
        const isSecure = window.location.protocol === 'https:';
        const wsProtocol = isSecure ? 'wss://' : 'ws://';
        const wsUrl = wsProtocol + window.location.host;
        const ws = new WebSocket(wsUrl);
        ws.onopen = () => ws.send(`ping:${Date.now()}`);
        ws.onmessage = (event) => {
            if (event.data.startsWith('pong:')) {
                setLatency(Date.now() - parseInt(event.data.split(':')[1], 10));
                ws.close();
            }
        };

        // --- UPLOAD TEST (to Cloudflare's Global Network) ---
        const runUploadTest = () => {
            const uploadXhr = new XMLHttpRequest();
            // Generate a smaller blob for the upload test as it's more sensitive to network conditions
            const uploadData = new Blob(Array(25 * 1024 * 1024).fill('a'), { type: 'application/octet-stream' });
            let uploadStartTime = Date.now();
            uploadXhr.upload.onprogress = (event) => {
                const duration = (Date.now() - uploadStartTime) / 1000;
                if (duration > 0) setUploadSpeed((event.loaded * 8) / (duration * 1000000));
            };
            uploadXhr.onload = () => {
                const duration = (Date.now() - uploadStartTime) / 1000;
                if(duration > 0) setUploadSpeed((uploadData.size * 8) / (duration * 1000000));
                setTestState('finished');
            };
            uploadXhr.open('POST', `${CLOUDFLARE_WORKER_URL}/upload`, true);
            uploadXhr.send(uploadData);
        };

        // --- DOWNLOAD TEST (from Cloudflare's Global Network) ---
        const downloadXhr = new XMLHttpRequest();
        downloadXhr.open('GET', `${CLOUDFLARE_WORKER_URL}/download`, true);
        downloadXhr.responseType = 'blob';
        let downloadStartTime = Date.now();
        downloadXhr.onprogress = (event) => {
            const duration = (Date.now() - downloadStartTime) / 1000;
            if (duration > 0) setDownloadSpeed((event.loaded * 8) / (duration * 1000000));
        };
        downloadXhr.onload = () => {
            const duration = (Date.now() - downloadStartTime) / 1000;
            if (duration > 0) setDownloadSpeed((downloadXhr.response.size * 8) / (duration * 1000000));
            setTimeout(() => runUploadTest(), 700);
        };
        downloadXhr.send();
    }, []);

    const handleLanguageChange = (e) => {
        i18n.changeLanguage(e.target.value);
    };

    const displaySpeed = (speed) => {
        if (!speed || speed <= 0) return '0';
        if (speed < 10) return speed.toFixed(2);
        if (speed < 100) return speed.toFixed(1);
        return Math.round(speed);
    };

    return (
        <div className="app-container">
            {/* ... rest of your JSX is identical ... */}
            <select className="language-selector" onChange={handleLanguageChange} value={i18n.language}>
                {Object.entries(supportedLanguages).map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                ))}
            </select>
            
            <div className="brand-logo">
                SP<span className="yellow">EE</span>D<span className="yellow">DD</span>
            </div>
            
            <h1>{t('your_internet_speed')}</h1>

            <div className="speed-container">
                <span className="speed-value">{displaySpeed(downloadSpeed)}</span>
                <span className="speed-units">Mbps</span>
            </div>
            
            {testState !== 'testing' && (
                <button className="start-button" onClick={runTest}>
                    {testState === 'idle' ? t('start') : t('restart')}
                </button>
            )}

            {(testState === 'testing' || testState === 'finished') && (
                 <div className="metrics-container">
                    <div className="metric-group">
                        <div className="metric-label">{t('latency')}</div>
                        <div className="metric-value-container">
                            <span className="metric-value">{latency}</span>
                            <span className="metric-units">ms</span>
                        </div>
                    </div>
                    <div className="metric-group">
                        <div className="metric-label">{t('upload')}</div>
                         <div className="metric-value-container">
                            <span className="metric-value">{displaySpeed(uploadSpeed)}</span>
                            <span className="metric-units">Mbps</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;